import React, { PropsWithChildren } from 'react';
import CheckIcon from '../../../images/check-icon.svg';
import ChevronDownIcon from '../../../images/chevron-down-icon.svg';
import ChevronUpIcon from '../../../images/cheveron-up-icon.svg';
import AiFillCaretDown from '../../../images/ai-fill-caret-down-icon.svg';
import throwOnMissing from '../utils/throw-on-missing';
import usePersistedId from '../utils/use-persisted-id';
import useDynamicImports, { LoadingStrategy } from '../../../hooks/use-dynamic-imports';
import FormAlert from '../form-alert';
import FormLabel from '../form-label';
import useFormState from '../form-provider/use-form-state';

type RadixSelectModule = typeof import('@radix-ui/react-select');

type SelectPrimitiveTypes = {
    Root: RadixSelectModule['Root'];
    SelectTrigger: RadixSelectModule['Trigger'];
    Value: RadixSelectModule['Value'];
    Icon: RadixSelectModule['Icon'];
    Content: RadixSelectModule['Content'];
    SelectScrollUpButton: RadixSelectModule['ScrollUpButton'];
    SelectScrollDownButton: RadixSelectModule['ScrollDownButton'];
    Viewport: RadixSelectModule['Viewport'];
    Item: RadixSelectModule['Item'];
    ItemText: RadixSelectModule['ItemText'];
    ItemIndicator: RadixSelectModule['ItemIndicator'];
};

const PrimitiveContext = React.createContext<SelectPrimitiveTypes>({} as unknown as SelectPrimitiveTypes);

type SelectOptionProps = PropsWithChildren<{
    value: string;
    disabled?: boolean;
    isPlaceholder?: boolean;
}>;

export const SelectOption = ({
    children,
    value,
    disabled,
    isPlaceholder,
}: SelectOptionProps): JSX.Element => {
    const Primitive = React.useContext(PrimitiveContext);
    return (
        <Primitive.Item
            value={value}
            disabled={disabled}
            aria-disabled={disabled}
            css={{
                fontSize: '16px',
                lineHeight: 1,
                color: 'var(--color-shades-black)',
                borderRadius: 3,
                display: 'flex',
                alignItems: 'center',
                padding: '8px 35px 8px 25px',
                userSelect: 'none',
                transition: 'box-shadow 0.2s',
                outline: 'none',
                boxShadow: '0 0 0 2px transparent',
                width: '100%',
                '&:focus-visible': {
                    boxShadow: '0 0 0 2px var(--color-states-focus)',
                    backgroundColor: 'var(--color-grey-300)',
                },
                '&[data-disabled]': {
                    color: 'var(--color-grey-400)',
                    pointerEvents: 'none',
                },
                '&:hover': {
                    backgroundColor: 'var(--color-grey-300)',
                },
                '@media (max-width: 767px)': {
                    fontSize: '14px',
                },
            }}
        >
            <Primitive.ItemText>{children}</Primitive.ItemText>
            {!isPlaceholder && (
                <Primitive.ItemIndicator
                    css={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <CheckIcon />
                </Primitive.ItemIndicator>
            )}
        </Primitive.Item>
    );
};

export interface SelectProps {
    name: string;
    value?: string;
    onChange?: (value: string) => void;
    onBlur?: (open: boolean) => void;
    placeholder?: string;
    required?: boolean;
    disabled?: boolean;
    className?: string;
    children: React.ReactNode;
}

const Select = ({
    children,
    onChange,
    onBlur,
    placeholder,
    name,
    required,
    disabled,
    value,
    className,
}: SelectProps): JSX.Element => {
    throwOnMissing(name, 'name', 'Select');
    const id = usePersistedId();
    const options = React.Children.toArray(children)
        .filter(React.isValidElement)
        .map((child) => {
            const el = child as React.ReactElement<SelectOptionProps>;
            return {
                value: el.props.value,
                label: el.props.children,
            };
        });

    const formContext = useFormState<string>({
        name,
        validators: {
            required,
        },
        defaultValue: (placeholder ? '' : options[0]?.value || ''),
        value,
        disabled,
    });

    const {
        hasLoaded,
        scripts: { SelectPrimitive },
    } = useDynamicImports(
        {
            SelectPrimitive: () => import('@radix-ui/react-select'),
        },
        LoadingStrategy.Hydrate,
    );

    const selectedValue = formContext?.value || value || (placeholder ? '' : options[0]?.value || '');

    const handleChange = (val: string): void => {
        if (onChange) onChange(val);
        formContext.setValue(val);
    };

    if (!hasLoaded) return <div />;

    const {
        Root,
        SelectTrigger,
        Value,
        Icon,
        Content,
        SelectScrollUpButton,
        SelectScrollDownButton,
        Viewport,
    } = SelectPrimitive;

    return (
        <PrimitiveContext.Provider value={SelectPrimitive}>
            <div css={{ position: 'relative', display: 'flex', flexDirection: 'column' }}>
                <Root
                    value={selectedValue}
                    onValueChange={handleChange}
                    onOpenChange={(open) => {
                        onBlur?.(open);
                    }}
                >
                    <SelectTrigger
                        id={id}
                        data-value={!!selectedValue}
                        css={{
                            '&[aria-expanded="true"] ~ div label, &[data-value="true"] ~ div label': {
                                opacity: '1',
                            },
                            '&[aria-expanded="true"], &[data-value="true"]': {
                                backgroundColor: 'transparent',
                                border: '2px solid var(--color-grey-200)',
                            },
                            border: formContext.error
                                ? '2px solid var(--color-states-error)'
                                : '2px solid transparent',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            userSelect: 'none',
                            padding: '12px',
                            width: '100%',
                            fontSize: '16px',
                            backgroundColor: 'var(--color-grey-200)',
                            color: 'var(--color-shades-black)',
                            borderRadius: '4px',
                            textAlign: 'left',
                            gap: '4px',
                        }}
                        className={className}
                        aria-label={name}
                        disabled={disabled}
                        aria-disabled={disabled}
                    >
                        <span aria-hidden="true" css={{ opacity: 0, clip: 'rect(0,0,0,0)', maxWidth: 0, maxHeight: '0' }}>
                            <Value />
                        </span>
                        <span css={{ width: '100%', display: 'inline-flex', justifyContent: 'space-between' }}>
                            {options.find(opt => opt.value === selectedValue)?.label || placeholder}
                            <Icon css={{ paddingLeft: '8px', marginTop: '7px' }}>
                                <AiFillCaretDown style={{ width: '10px', height: '10px' }} />
                            </Icon>
                        </span>
                    </SelectTrigger>

                    {placeholder && selectedValue !== placeholder && <FormLabel id={id}>{placeholder}</FormLabel>}

                    <Content
                        css={{
                            marginTop: 40,
                            alignSelf: 'end',
                            width: 'calc(100% - 16px)',
                            overflow: 'hidden',
                            backgroundColor: 'var(--color-grey-200)',
                            color: 'var(--color-shades-black)',
                            borderRadius: 6,
                            boxShadow: '0px 10px 38px -10px rgba(22, 23, 24, 0.35), 0px 10px 20px -15px rgba(22, 23, 24, 0.2)',
                            zIndex: 300,
                        }}
                    >
                        <SelectScrollUpButton
                            css={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: 25,
                                margin: '0 -1px',
                                backgroundColor: 'var(--color-grey-200)',
                                color: 'var(--color-shades-black)',
                                cursor: 'default',
                            }}
                        >
                            <ChevronUpIcon />
                        </SelectScrollUpButton>
                        <Viewport css={{ padding: '5px' }}>
                            {React.Children.map(children, child => React.cloneElement(child as React.ReactElement, {
                                Primitive: SelectPrimitive,
                            }))}
                        </Viewport>
                        <SelectScrollDownButton
                            css={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: 25,
                                margin: '0 -1px',
                                backgroundColor: 'var(--color-grey-200)',
                                color: 'var(--color-shades-black)',
                                cursor: 'default',
                            }}
                        >
                            <ChevronDownIcon />
                        </SelectScrollDownButton>
                    </Content>
                </Root>
                {formContext.error && <FormAlert>{formContext.error}</FormAlert>}
            </div>
        </PrimitiveContext.Provider>
    );
};

export default Select;
